import { memoize } from "lodash";
import { UserManager } from "oidc-client-ts";

import {
  getCustomOidcEnabled,
  getCustomOidcSettings,
} from "../config/customOidc";

/**
 * Returns the singleton UserManager instance for OIDC authentication.
 *
 * This UserManager is shared across the entire application, both inside React context
 * (via AuthProvider) and outside (e.g., in useAuthFetch).
 *
 * @returns UserManager instance, or null if OIDC is not enabled
 */
export const getUserManager = memoize(() => {
  // Return null if OIDC is disabled
  if (!getCustomOidcEnabled()) {
    return null;
  }

  // Create new instance with settings from config
  const settings = getCustomOidcSettings();
  return new UserManager(settings);
});

/**
 * Retrieves the current access token from the UserManager.
 *
 * @returns Access token string, or null if not authenticated or refresh fails
 */
export const getAccessToken = async (): Promise<string | null> => {
  const manager = getUserManager();
  if (!manager) {
    return null;
  }

  try {
    const user = await manager.getUser();

    if (!user) {
      return null;
    }

    // If token is expired, attempt silent refresh
    if (user.expired) {
      try {
        const refreshed = await manager.signinSilent();
        return refreshed?.access_token ?? null;
      } catch {
        // eslint-disable-next-line no-console
        console.error("Failed to refresh access token");
        return null;
      }
    }

    return user.access_token ?? null;
  } catch {
    return null;
  }
};

/**
 * Retrieves the current OIDC ID token from the UserManager.
 *
 * @returns ID token string, or null if not authenticated
 */
export const getOidcIdToken = async (): Promise<string | null> => {
  const manager = getUserManager();
  if (!manager) {
    return null;
  }

  try {
    const user = await manager.getUser();
    return user?.id_token ?? null;
  } catch {
    return null;
  }
};
